import { CartAddEvent, ThemeEvents } from '@theme/events';

// A single queue across cards prevents out-of-order cart totals/section HTML.
// Do not retry mutation requests automatically: an interrupted response can still mean an added item.
let cartQueue = Promise.resolve();
const route = (name) => `${window.Shopify?.routes?.root || '/'}${name}`;
const toast = () => document.querySelector('cj-cart-toast');
const readCart = async () => {
  const response = await fetch(route('cart.js'), { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (!response.ok) throw new Error('No se pudo actualizar el carrito.');
  return response.json();
};
const publish = (cart, item, data = {}) => {
  const knownCount = Number.isFinite(cart?.item_count);
  document.dispatchEvent(new CartAddEvent(cart, String(item?.id || ''), {
    source: 'cj-card-add', itemCount: knownCount ? cart.item_count : undefined, cjCountUnknown: !knownCount, productId: String(item?.product_id || ''),
    variantId: String(item?.id || ''), cjSuppressDrawer: true, ...data,
  }));
};

class CJCardAdd extends HTMLElement {
  #abort;
  #busy = false;
  #variant = null;
  #pendingVariant = false;
  #variants = [];
  connectedCallback() {
    // Recommendations arrive by Section Rendering and do not reliably execute
    // the card block's inline relocation script. Place this component itself.
    const content = this.closest('.product-card__content.pf-card-meta-frame');
    // Search renders its footer in Liquid. Never pull its action out of that
    // footer; morphing recent/results markup must not change the coordinates.
    if (content && !content.classList.contains('cj-search-card')) {
      content.classList.add('cj-direct-card');
      if (this.parentElement !== content) { content.append(this); return; }
      this.dataset.cjInCardFooter = 'true';
    }
    this.#abort?.abort(); this.#abort = new AbortController();
    const signal = this.#abort.signal;
    try { this.#variants = JSON.parse(this.querySelector('[data-cj-variants]')?.textContent || '[]'); } catch { this.#variants = []; }
    const card = this.closest('product-card');
    card?.addEventListener(ThemeEvents.variantSelected, () => { this.#pendingVariant = true; }, { capture: true, signal });
    card?.addEventListener(ThemeEvents.variantUpdate, (event) => {
      this.#pendingVariant = false;
      this.#variant = event.detail?.resource || null;
      this.#setAvailable();
    }, { capture: true, signal });
    this.addEventListener('submit', this.#submit, { signal });
    this.querySelector('a[data-cj-add-trigger]')?.addEventListener('click', this.#submit, { signal });
    this.#setAvailable();
  }
  disconnectedCallback() { this.#abort?.abort(); /* Never abort an in-flight cart mutation. */ }
  #getVariant() {
    if (this.dataset.requiresPlan === 'true') return null;
    if (Number(this.dataset.variantsCount) === 1) return this.#variants[0] || null;
    const card = this.closest('product-card');
    // A complete, explicit swatch selection is safe only for single-option products.
    if (Number(this.dataset.optionsCount) === 1) {
      const selected = card?.querySelector('input[type="radio"][data-variant-id]:checked');
      if (selected && !card.hasAttribute('data-no-swatch-selected')) {
        return this.#variants.find(v => String(v.id) === selected.dataset.variantId) || null;
      }
    }
    if (this.#pendingVariant) return null;
    const selectedId = this.#variant?.id || this.dataset.selectedVariant;
    return this.#variants.find(v => String(v.id) === String(selectedId)) || null;
  }
  #setAvailable() {
    const button = this.querySelector('[data-cj-add-trigger]'); if (!button) return;
    const v = this.#getVariant();
    const soldOut = this.dataset.available === 'false' || (v && !v.available);
    button.setAttribute('aria-disabled', String(Boolean(soldOut || this.#busy)));
    if (button instanceof HTMLButtonElement) button.disabled = Boolean(soldOut || this.#busy);
    const label = soldOut ? 'Agotado' : v ? 'Agregar al carrito' : 'Elegir opciones';
    button.title = label; button.setAttribute('aria-label', `${label}: ${this.dataset.productTitle || ''}`);
  }
  #submit = (event) => {
    if (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return;
    if (this.dataset.available === 'false') { event.preventDefault(); return; }
    const v = this.#getVariant();
    if (!v || this.dataset.requiresPlan === 'true') {
      if (event.type === 'submit') {
        event.preventDefault(); window.location.assign(this.closest('product-card')?.getProductCardLink?.()?.href || this.dataset.productUrl);
      }
      // An anchor naturally opens the product page; never substitute the first variant.
      return;
    }
    event.preventDefault(); event.stopPropagation();
    if (this.#busy || !v.available) return;
    const form = this.querySelector('form'); if (!form || !form.checkValidity()) return;
    const feedback = this.closest('[data-cj-search-card]')?.querySelector('[data-cj-search-feedback]');
    if (feedback) { feedback.hidden = true; feedback.textContent = ''; }
    this.#busy = true; this.#setAvailable();
    this.querySelector('[data-cj-add-trigger]')?.setAttribute('aria-busy', 'true');
    const data = new FormData(form); data.set('id', String(v.id)); data.set('quantity', String(v.min || 1));
    const title = this.dataset.productTitle;
    const job = () => this.#add(data, title);
    cartQueue = cartQueue.then(job, job).finally(() => {
      this.#busy = false; this.querySelector('[data-cj-add-trigger]')?.removeAttribute('aria-busy'); this.#setAvailable();
    });
  };
  #showError(message) {
    const feedback = this.closest('[data-cj-search-card]')?.querySelector('[data-cj-search-feedback]');
    if (feedback) {
      // Keep a failed add visible inside the modal top layer without closing search.
      feedback.hidden = false; feedback.textContent = message;
    } else { toast()?.show({error:message, cartSynced:false}); }
  }
  async #add(data, title) {
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map(el => el.dataset.sectionId))].filter(Boolean).slice(0,5);
    if (sectionIds.length) data.set('sections', sectionIds.join(','));
    data.set('sections_url', `${location.pathname}${location.search}`);
    let response, result;
    try {
      response = await fetch(window.Theme?.routes?.cart_add_url || route('cart/add.js'), { method:'POST', body:data, headers:{Accept:'application/json'} });
      result = await response.json();
    } catch {
      this.#showError('No pudimos confirmar el agregado. Revisa tu carrito antes de intentarlo de nuevo.');
      try { publish(await readCart(), null, {didError:true}); } catch { /* Keep the explicit unconfirmed status. */ }
      return;
    }
    if (!response.ok || result.status) {
      const message = typeof result.description === 'string' ? result.description : typeof result.message === 'string' ? result.message : 'No se pudo agregar este producto. Revisa su disponibilidad.';
      this.#showError(message);
      try { publish(await readCart(), null, { didError:true }); } catch { /* The server message remains visible. */ }
      return;
    }
    const item = result.items?.[0] || result;
    let cart = null;
    try { cart = await readCart(); publish(cart, item, {sections:result.sections}); }
    catch {
      // The ADD succeeded even if the following read did not. Do not mislabel it as a failed add or retry.
      publish(null, item, {sections:result.sections});
    }
    const sectionsSynced = sectionIds.every(id => typeof result.sections?.[id] === 'string');
    // Rich search results contain real product forms outside the query form.
    // On confirmed success close only the search dialog, so the existing toast
    // and native cart can be reached outside the dialog's top layer.
    if (this.closest('[data-cj-search-card]')) this.closest('dialog-component')?.closeDialog?.();
    toast()?.show({ item, title, cartSynced:Boolean(cart) && sectionsSynced });
  }
}

class CJCartToast extends HTMLElement {
  #abort;
  #origin;
  connectedCallback() {
    this.#abort?.abort();this.#abort = new AbortController(); const signal=this.#abort.signal;
    this.querySelector('[data-cj-toast-close]')?.addEventListener('click',this.close,{signal});
    this.querySelector('[data-cj-toast-cart]')?.addEventListener('click',(event)=>{
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || this.dataset.cartSynced === 'false') return;
      const drawer=document.querySelector('cart-drawer-component');
      if (drawer && typeof drawer.open === 'function') { event.preventDefault(); this.close(); drawer.open(); }
    },{signal});
    document.addEventListener('keydown',(event)=>{ if(event.key==='Escape' && !this.hidden) { this.close(); } },{signal});
  }
  disconnectedCallback(){this.#abort?.abort();}
  show({item,title,error,cartSynced=true}) {
    if (!this.contains(document.activeElement)) this.#origin=document.activeElement;
    this.dataset.cartSynced=String(cartSynced);
    const status=this.querySelector('[data-cj-toast-status]');
    const announcement=document.getElementById('CJCartAnnouncement');
    const product=this.querySelector('[data-cj-toast-product]');
    const img=this.querySelector('[data-cj-toast-image]');
    product.hidden=Boolean(error);
    this.hidden=false;
    status.textContent=error || (cartSynced ? 'Producto agregado al carrito con éxito' : 'Producto agregado. Abre el carrito para ver el total actualizado.');
    if (announcement) { announcement.textContent=''; requestAnimationFrame(()=>{ announcement.textContent=status.textContent; }); }
    if(!error){
      this.querySelector('[data-cj-toast-title]').textContent=item?.product_title || title || item?.title || '';
      this.querySelector('[data-cj-toast-variant]').textContent=item?.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
      const url=item?.featured_image?.url || item?.image;
      if(url){img.src=url;img.hidden=false;}else{img.hidden=true;img.removeAttribute('src');}
    }
    // Persistent and non-modal: no focus stealing or time limit for reading the confirmation.
  }
  close=()=>{const restore=this.contains(document.activeElement);this.hidden=true;if(restore && this.#origin?.isConnected)this.#origin.focus?.({preventScroll:true});};
}
if(!customElements.get('cj-card-add')) customElements.define('cj-card-add',CJCardAdd);
if(!customElements.get('cj-cart-toast')) customElements.define('cj-cart-toast',CJCartToast);
